// Modules
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import GlobalPayPayFieldsForm from './GlobalPayPayFieldsForm';
import CardRadioButton from 'components/Common/CardRadio/CardRadioButton';
import { toast } from 'react-toastify';
import { v4 as uuid } from 'uuid';
import { upsertGlobalPayCustomerWithNewCreditCard } from 'utils/api/customerApi';
import Label from 'components/Common/Label';

// API
import { updatePaymentMethod } from 'utils/api/customerApi';
import {
  createDraftTransaction,
  createTerminalSale,
  createPayFieldsSale,
  createSavedCardSale,
  cancelTerminalSale,
} from 'utils/api/globalpayApi';

import { postTransactionReceipt } from '../../../../utils/api/transactionPublicApi';

// Hooks
import useStepHistory from 'utils/hooks/useStepHistory';

// Components
import { Modal, Button } from 'react-bootstrap';
import Loading from 'components/Common/Loading';
import PaymentInfoStep from './PaymentInfoStep';
import PaymentResponseStep from './PaymentResponseStep';
import SelectCardStep from './CardSelect/SelectCardStep';

// Helpers
import { cents } from 'utils/helpers/currencyFormat';
import { getPhoneDigitsOnly } from 'utils/helpers/phoneNumberValidate';
import { capitalizeName, isFullName } from 'utils/helpers/names';

// Constants
import PaymentStates from 'constants/PaymentStates';

// CSS
import styles from './GlobalPayModal.module.scss';

const FormSteps = Object.freeze({
  PAYMENT_INFO: 0,
  SELECT_CARD: 1,
  GLOBALPAY_FORM: 2,
  GLOBALPAY_TERMINAL_SELECT: 3,
  GLOBALPAY_TERMINAL: 4,
  PAYMENT_RESPONSE: 5,
  PAYMENT_FAILED: 6,
  NEW_USER_ADDRESS: 7,
});

export default function GlobalPayModal(props) {
  const {
    showModal,
    setShowModal,
    triggerUpdate,
    payByTerminalEnabled,
    globalPayDeviceIds,
  } = props;

  const [selectedPetOwner, setSelectedPetOwner] = useState(null);
  const [selectedPaymentMethodIndex, setSelectedPaymentMethodIndex] =
    useState(0);
  const [staffMember, setStaffMember] = useState('Choose staff member...');
  const [petOwnerName, setPetOwnerName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [description, setDescription] = useState('');
  const [totalAmount, setTotalAmount] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [waitingForTerminal, setWaitingForTerminal] = useState(false);
  const [draftTransactionId, setDraftTransactionId] = useState({});
  const [{ currentStep }, { prevStep, setStep }] = useStepHistory();
  const [currentPaymentState, setCurrentPaymentState] = useState('');
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [lastFourDigits, setLastFourDigits] = useState('XXXX');
  const [showReceiptOptions, setShowReceiptOptions] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [newCustomerId, setNewCustomerId] = useState('');
  const [isTerminal, setIsTerminal] = useState('');
  const [shouldSaveCard, setShouldSaveCard] = useState(false);
  const [payFieldToken, setPayFieldToken] = useState('');
  const [successWarningMessage, setSuccessWarningMessage] = useState('');

  // temp info for the address modal
  const [tempSaleResponseInfo, setTempSaleResponseInfo] = useState();
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [addressCity, setAddressCity] = useState('');
  const [addressState, setAddressState] = useState('');
  const [addressZip, setAddressZip] = useState('');

  const payWithSavedCardEnabled = useSelector(
    (s) => s.loggedPractice.payWithSavedCards
  );

  useEffect(() => {
    if (selectedDeviceId === '' && globalPayDeviceIds) {
      if (globalPayDeviceIds.length > 0)
        setSelectedDeviceId(globalPayDeviceIds[0][0]);
    }
  }, [globalPayDeviceIds, selectedDeviceId]);

  useEffect(() => {
    if (payFieldToken !== '') {
      handlePayFieldInformationAccepted();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payFieldToken]);

  // Fields used for validation
  const STAFF_MEMBER_DEFINITION = {
    field: 'staffMember',
    value: staffMember,
    validationMessage: 'Select a staff member',
  };
  const TOTAL_AMOUNT_DEFINITION = {
    field: 'totalAmount',
    value: totalAmount,
    validationMessage: 'Enter a non-zero amount',
  };
  const DESCRIPTION_DEFINITION = {
    field: 'description',
    value: description,
    validationMessage: 'Enter a description',
  };
  const PET_OWNER_NAME_DEFINITION = {
    field: 'petOwnerName',
    value: petOwnerName,
    validationMessage: "Enter a pet owner's full name",
  };
  const PHONE_NUMBER_DEFINITION = {
    field: 'phoneNumber',
    value: phoneNumber,
    validationMessage: 'Enter a valid phone number',
  };

  const step1Validation = [
    STAFF_MEMBER_DEFINITION,
    TOTAL_AMOUNT_DEFINITION,
    DESCRIPTION_DEFINITION,
    PET_OWNER_NAME_DEFINITION,
    PHONE_NUMBER_DEFINITION,
  ];

  const validateGlobalPayStep = (stepValidation) => {
    const validationResult = validateStep(
      stepValidation,
      payWithSavedCardEnabled
    );

    setValidationErrors(validationResult);
    return Object.keys(validationResult).length === 0;
  };

  const getBaseTransactionDetails = () => {
    return {
      petOwnerName: capitalizeName(petOwnerName),
      petOwnerPhone: phoneNumber ? `+1${getPhoneDigitsOnly(phoneNumber)}` : '',
      invoiceId: description,
      amount: cents(totalAmount, { resultType: 'number' }),
      staffMemberId: staffMember,
    };
  };

  const submitPaymentInfo = async (_isTerminal) => {
    const stepIsValid = validateGlobalPayStep(step1Validation);

    if (!stepIsValid) return;

    let newStep =
      selectedPetOwner !== null &&
      payWithSavedCardEnabled &&
      selectedPetOwner?.payment_methods?.length > 0 &&
      currentStep !== FormSteps.SELECT_CARD
        ? FormSteps.SELECT_CARD
        : _isTerminal
        ? FormSteps.GLOBALPAY_TERMINAL
        : FormSteps.GLOBALPAY_FORM;

    if (
      newStep === FormSteps.GLOBALPAY_TERMINAL &&
      globalPayDeviceIds &&
      globalPayDeviceIds.length > 1
    ) {
      newStep = FormSteps.GLOBALPAY_TERMINAL_SELECT;
    }

    setStep(newStep);
    setIsTerminal(_isTerminal);

    let customerId = uuid();
    if (payWithSavedCardEnabled) {
      if (selectedPetOwner) {
        customerId = selectedPetOwner.customer_id;
      } else {
        setNewCustomerId(customerId);
      }
    }

    if (newStep === FormSteps.GLOBALPAY_FORM) {
      // set it here so that when the script loads it sets it to false, might have issues in the future
      setFormLoading(true);
    } else if (newStep === FormSteps.GLOBALPAY_TERMINAL) {
      // if its the terminal we just want to send the request immediately
      await payWithTerminal(customerId);
    }
  };

  const payWithTerminal = async (customerId) => {
    try {
      setWaitingForTerminal(true);
      const transactionDetails = getBaseTransactionDetails();
      transactionDetails.customerId = customerId;
      transactionDetails.globalPayDeviceId = selectedDeviceId;
      const draft = await createDraftTransaction(transactionDetails);
      setDraftTransactionId(draft.transactionId);

      const saleRequest = {
        shouldCreateToken: shouldSaveCard,
        transactionId: draft.transactionId,
        ...transactionDetails,
      };

      const saleResponse = await createTerminalSale(saleRequest);

      if (saleResponse && saleResponse.status === 'succeeded') {
        setSuccessWarningMessage(saleResponse.successWarningMessage ?? '');
        await tryAddingSavedCard(saleResponse);
      } else {
        setErrorMessage(saleResponse.message);
        setCurrentPaymentState(PaymentStates.Failed);
        setWaitingForTerminal(false);
        setStep(FormSteps.PAYMENT_RESPONSE);
      }
    } catch (error) {
      console.error(error);
      setErrorMessage(error.errorMessage);
      setCurrentPaymentState(PaymentStates.Failed);
      setWaitingForTerminal(false);
      setStep(FormSteps.PAYMENT_RESPONSE);
    }
  };

  const payWithSelectedCard = async () => {
    try {
      setFormLoading(true);
      const creditCard =
        selectedPetOwner.payment_methods[selectedPaymentMethodIndex];
      const transactionDetails = getBaseTransactionDetails();

      const saleRequest = {
        creditCardId: creditCard.payment_method_token,
        addressInfo: selectedPetOwner.addresses[0],
        ...transactionDetails,
      };

      // no draft transaction created ahead of time
      const saleResponse = await createSavedCardSale(saleRequest);

      if (saleResponse && saleResponse.status === 'succeeded') {
        setDraftTransactionId(saleResponse.id);
        setSuccessWarningMessage(saleResponse.successWarningMessage ?? '');
        await checkSavedCardUpdatedInformation(saleResponse, creditCard);
      } else {
        setCurrentPaymentState(PaymentStates.Failed);
        setFormLoading(false);
        setStep(FormSteps.PAYMENT_RESPONSE);
      }
    } catch (error) {
      console.error(error);
      setErrorMessage(error.errorMessage);
      setCurrentPaymentState(PaymentStates.Failed);
      setFormLoading(false);
      setStep(FormSteps.PAYMENT_RESPONSE);
    }
  };

  const handlePayWithSelectedCard = async () => {
    selectedPaymentMethodIndex >= selectedPetOwner?.payment_methods?.length
      ? await submitPaymentInfo(isTerminal)
      : await payWithSelectedCard();
  };

  const handlePayFieldInformationAccepted = async () => {
    try {
      setFormLoading(true);

      const transactionDetails = getBaseTransactionDetails();
      // transactionDetails.customerId = customerId;
      const draft = await createDraftTransaction(transactionDetails);
      setDraftTransactionId(draft.transactionId);

      const addressInfo = {
        address_1: addressLine1,
        address_2: addressLine2,
        city: addressCity,
        state: addressState,
        zip: addressZip,
      };

      const saleRequest = {
        transactionId: draft.transactionId,
        temporaryToken: payFieldToken,
        shouldCreateToken: shouldSaveCard,
        addressInfo: addressInfo,
        ...transactionDetails,
      };

      const saleResponse = await createPayFieldsSale(saleRequest);

      if (saleResponse && saleResponse.status === 'succeeded') {
        setSuccessWarningMessage(saleResponse.successWarningMessage ?? '');
        // this handles if its not a save card function
        await tryAddingSavedCard(saleResponse);
      } else {
        // setErrorMessage(msg.errorMessage);
        setCurrentPaymentState(PaymentStates.Failed);
        setFormLoading(false);
        setStep(FormSteps.PAYMENT_RESPONSE);
      }
    } catch (error) {
      console.error(error);
      setErrorMessage(error.errorMessage);
      setCurrentPaymentState(PaymentStates.Failed);
      setFormLoading(false);
      setStep(FormSteps.PAYMENT_RESPONSE);
    }
  };

  const tryAddingSavedCard = async (saleResponse) => {
    if (payWithSavedCardEnabled && shouldSaveCard && saleResponse.gpToken) {
      // if it's adding a new contact and address information hasn't been saved
      if (!selectedPetOwner && (addressLine1 === '' || addressZip === '')) {
        setTempSaleResponseInfo(saleResponse);

        setStep(FormSteps.NEW_USER_ADDRESS);
        return;
      }

      const paymentMethod = {
        last_four: saleResponse.last4,
        payment_method_token: saleResponse.gpToken,
        full_name: saleResponse.holderFullName,
        card_type: saleResponse.brand,
        expiration_month: saleResponse.exp_month,
        expiration_year: saleResponse.exp_year,
      };

      const addressInfo = {
        address_1: addressLine1,
        address_2: addressLine2,
        city: addressCity,
        state: addressState,
        zip: addressZip,
      };

      const customerId = selectedPetOwner?.customer_id || newCustomerId;
      const customerName = petOwnerName ?? saleResponse.holderFullName;

      try {
        const newPaymentMethod = await upsertGlobalPayCustomerWithNewCreditCard(
          selectedPetOwner,
          customerId,
          customerName,
          phoneNumber,
          paymentMethod,
          addressInfo
        );
        console.log(newPaymentMethod);
      } catch (err) {
        toast.error(
          'Encountered an error when trying to update customer saved card information.'
        );
      }
    }

    setLastFourDigits(saleResponse.last4);
    setCurrentPaymentState(PaymentStates.Successful);
    triggerUpdate();
    setFormLoading(false);
    setStep(FormSteps.PAYMENT_RESPONSE);
  };

  const checkSavedCardUpdatedInformation = async (saleResponse, creditCard) => {
    if (
      payWithSavedCardEnabled &&
      creditCard.payment_method_token &&
      saleResponse.last4 &&
      saleResponse.exp_year &&
      saleResponse.exp_month
    ) {
      if (
        creditCard.last_four !== saleResponse.last4 ||
        creditCard.expiration_year !== saleResponse.exp_year ||
        creditCard.expiration_month !== saleResponse.exp_month
      ) {
        const paymentMethod = {
          last_four: saleResponse.last4,
          payment_method_token: creditCard.payment_method_token,
          full_name: saleResponse.holderFullName,
          card_type: saleResponse.brand,
          expiration_month: saleResponse.exp_month,
          expiration_year: saleResponse.exp_year,
        };

        const customerId = selectedPetOwner?.customer_id || newCustomerId;

        try {
          const newPaymentMethod = await updatePaymentMethod(
            paymentMethod,
            customerId,
            creditCard.id
          );
          console.log(newPaymentMethod);
        } catch (err) {
          toast.error(
            'Encountered an error when trying to update customer saved card information.'
          );
        }
      }
    }

    setLastFourDigits(saleResponse.last4);
    setCurrentPaymentState(PaymentStates.Successful);
    triggerUpdate();
    setFormLoading(false);
    setStep(FormSteps.PAYMENT_RESPONSE);
  };

  const handleCancelTerminalTransaction = async () => {
    try {
      const cancelResult = await cancelTerminalSale(draftTransactionId);
      console.log(cancelResult);
    } catch (error) {
      console.error('Error when trying to cancel terminal sale: ', error);
    }
  };

  const tryAgain = async () => {
    try {
      if (
        selectedPaymentMethodIndex < selectedPetOwner?.payment_methods?.length
      ) {
        setStep(FormSteps.SELECT_CARD);
      } else if (isTerminal) {
        setStep(FormSteps.GLOBALPAY_TERMINAL);
        await payWithTerminal(selectedPetOwner?.customer_id);
      } else {
        setStep(FormSteps.GLOBALPAY_FORM);
      }
    } catch (err) {
      toast.error('Encountered an error reloading the payment form');
    }
  };

  return (
    <Modal
      show={showModal}
      onHide={setShowModal}
      size='md'
      aria-labelledby={`pay-in-full-modal`}
      centered
    >
      <Modal.Header>
        <Modal.Title>
          {currentStep === FormSteps.PAYMENT_INFO && 'Payment Information'}
          {currentStep === FormSteps.SELECT_CARD && 'Pay with Saved Card'}
          {currentStep === FormSteps.GLOBALPAY_FORM && 'Pay by Card'}
          {currentStep === FormSteps.GLOBALPAY_TERMINAL_SELECT &&
            'Select a Terminal'}
          {currentStep === FormSteps.GLOBALPAY_TERMINAL &&
            'Waiting for Terminal'}
          {currentStep === FormSteps.NEW_USER_ADDRESS &&
            'Add Address for New Customer'}
          {currentStep === FormSteps.PAYMENT_RESPONSE &&
            currentPaymentState === PaymentStates.Failed &&
            `Payment Failed`}
          {currentStep === FormSteps.PAYMENT_RESPONSE &&
            currentPaymentState === PaymentStates.Successful &&
            `Payment Successful`}
          {currentStep === FormSteps.PAYMENT_RESPONSE &&
            currentPaymentState === PaymentStates.Completed &&
            `Payment Completed`}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {currentStep === FormSteps.PAYMENT_INFO && !waitingForTerminal && (
          <PaymentInfoStep
            staffMember={staffMember}
            setStaffMember={setStaffMember}
            petOwnerName={petOwnerName}
            setPetOwnerName={setPetOwnerName}
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
            description={description}
            setDescription={setDescription}
            totalAmount={totalAmount}
            setTotalAmount={setTotalAmount}
            formLoading={formLoading}
            setFormLoading={setFormLoading}
            validationErrors={validationErrors}
            selectedPetOwner={selectedPetOwner}
            setSelectedPetOwner={setSelectedPetOwner}
            shouldSaveCard={shouldSaveCard}
            setShouldSaveCard={setShouldSaveCard}
            isGlobalPay={true}
            enablePhoneNumber={true}
          />
        )}

        {currentStep === FormSteps.SELECT_CARD && (
          <>
            <SelectCardStep
              selectedPaymentMethodIndex={selectedPaymentMethodIndex}
              setSelectedPaymentMethodIndex={setSelectedPaymentMethodIndex}
              paymentMethods={selectedPetOwner?.payment_methods}
              totalAmount={totalAmount}
              formLoading={formLoading}
            />
          </>
        )}

        {currentStep === FormSteps.GLOBALPAY_FORM && (
          <div className={styles.globalPayHostedForm}>
            <GlobalPayPayFieldsForm
              handleCancel={prevStep}
              handlePayFieldInformationAccepted={
                // setting the temp token which triggers useeffect so no stale state from event handler
                (temp_token) => setPayFieldToken(temp_token)
              }
              formLoading={formLoading}
              setFormLoading={setFormLoading}
              displayAddress={true}
              addressLine1={addressLine1}
              setAddressLine1={setAddressLine1}
              addressLine2={addressLine2}
              setAddressLine2={setAddressLine2}
              addressCity={addressCity}
              setAddressCity={setAddressCity}
              addressState={addressState}
              setAddressState={setAddressState}
              addressZip={addressZip}
              setAddressZip={setAddressZip}
            />

            {formLoading && (
              <div className={styles.globalPayLoading}>
                <Loading />
              </div>
            )}
          </div>
        )}

        {currentStep === FormSteps.GLOBALPAY_TERMINAL && (
          <div className={styles.globalPayHostedForm}>
            {waitingForTerminal && (
              <div className={styles.globalPayLoading}>
                <Loading />
              </div>
            )}
          </div>
        )}

        {currentStep === FormSteps.GLOBALPAY_TERMINAL_SELECT && (
          <div className={styles.globalPayTerminalList}>
            {globalPayDeviceIds &&
              globalPayDeviceIds.map((val, i) => (
                <CardRadioButton
                  key={i}
                  onClick={() => {
                    setSelectedDeviceId(val[0]);
                  }}
                  checked={val[0] === selectedDeviceId}
                >
                  {val[1]} - {val[0]}
                </CardRadioButton>
              ))}
          </div>
        )}

        {currentStep === FormSteps.NEW_USER_ADDRESS && (
          <div className={styles.globalPayAddressForm}>
            <div>
              <Label text='Billing Address' hasError={addressLine1 === ''} />
              <div className={styles.spaceChildrenEvenly}>
                <input
                  type='text'
                  placeholder='Address Line 1'
                  autocomplete='disabled'
                  onChange={(e) => setAddressLine1(e.currentTarget.value)}
                />
                <input
                  type='text'
                  placeholder='Address Line 2'
                  autocomplete='disabled'
                  onChange={(e) => setAddressLine2(e.currentTarget.value)}
                />
              </div>
            </div>

            <div className={styles.globalPayPartialAddressInput}>
              <div>
                <Label text='City' hasError={addressCity === ''} />
                <input
                  className={styles.addressInputField}
                  type='text'
                  placeholder='City'
                  autocomplete='disabled'
                  onChange={(e) => setAddressCity(e.currentTarget.value)}
                />
              </div>

              <div>
                <Label text='State' hasError={addressState === ''} />
                <input
                  className={styles.addressInputField}
                  type='text'
                  placeholder='State'
                  autocomplete='disabled'
                  onChange={(e) => setAddressState(e.currentTarget.value)}
                />
              </div>

              <div>
                <Label text='Zip' hasError={addressZip === ''} />
                <input
                  className={styles.addressInputField}
                  type='text'
                  placeholder='Zip'
                  autocomplete='disabled'
                  onChange={(e) => setAddressZip(e.currentTarget.value)}
                />
              </div>
            </div>
          </div>
        )}

        {currentStep === FormSteps.PAYMENT_RESPONSE && (
          <PaymentResponseStep
            totalAmount={totalAmount}
            formLoading={formLoading}
            currentPaymentState={currentPaymentState}
            errorMessage={errorMessage}
            setCurrentPaymentState={setCurrentPaymentState}
            lastFourDigits={lastFourDigits}
            postTransactionReceipt={postTransactionReceipt}
            showReceiptOptions={showReceiptOptions}
            setShowReceiptOptions={setShowReceiptOptions}
            processedTransactionId={{ current: draftTransactionId }} // this is how the data structure is set up in PaymentResponseStep
            autoReceiptPhoneNumber={selectedPetOwner ? phoneNumber : null}
            warningMessage={successWarningMessage}
          />
        )}

        {currentStep === FormSteps.PAYMENT_FAILED && (
          <div>
            <h2>Error</h2>
            <p>There was an error with the payment. Please contact support.</p>
            <p>PayHero Transaction ID: {draftTransactionId}</p>
          </div>
        )}
      </Modal.Body>

      {currentStep === FormSteps.GLOBALPAY_TERMINAL && payByTerminalEnabled && (
        <Modal.Footer>
          <Button
            variant='light'
            onClick={handleCancelTerminalTransaction}
            disabled={false}
          >
            Cancel
          </Button>
        </Modal.Footer>
      )}

      {currentStep === FormSteps.PAYMENT_INFO && payByTerminalEnabled && (
        <Modal.Footer>
          <Button
            variant='light'
            onClick={() => {
              setShowModal();
            }}
            disabled={false}
          >
            Cancel
          </Button>

          <Button
            variant='secondary'
            onClick={() => {
              submitPaymentInfo(true);
            }}
            disabled={formLoading}
          >
            Send to Terminal
          </Button>
          <Button
            variant='secondary'
            onClick={() => {
              submitPaymentInfo(false);
            }}
            disabled={formLoading}
          >
            Manual Entry
          </Button>
        </Modal.Footer>
      )}

      {currentStep === FormSteps.PAYMENT_INFO && !payByTerminalEnabled && (
        <Modal.Footer>
          <Button
            variant='light'
            onClick={() => {
              setShowModal();
            }}
            disabled={false}
          >
            Cancel
          </Button>

          <Button
            variant='secondary'
            onClick={() => {
              submitPaymentInfo(false);
            }}
            disabled={formLoading}
          >
            Next
          </Button>
        </Modal.Footer>
      )}

      {currentStep === FormSteps.SELECT_CARD && (
        <Modal.Footer>
          <Button
            variant='light'
            onClick={() => {
              prevStep();
            }}
            disabled={false}
          >
            Back
          </Button>

          <Button
            variant='secondary'
            onClick={handlePayWithSelectedCard}
            disabled={formLoading}
          >
            {selectedPaymentMethodIndex <
            selectedPetOwner?.payment_methods?.length
              ? 'Pay Now'
              : 'Next'}
          </Button>
        </Modal.Footer>
      )}

      {currentStep === FormSteps.GLOBALPAY_TERMINAL_SELECT && (
        <Modal.Footer>
          <Button
            variant='light'
            onClick={() => {
              prevStep();
            }}
            disabled={false}
          >
            Back
          </Button>

          <Button
            variant='secondary'
            onClick={() => {
              setStep(FormSteps.GLOBALPAY_TERMINAL);
              payWithTerminal(selectedPetOwner?.customer_id);
            }}
            disabled={selectedDeviceId === ''}
          >
            Next
          </Button>
        </Modal.Footer>
      )}

      {currentStep === FormSteps.NEW_USER_ADDRESS && (
        <Modal.Footer>
          <Button
            variant='light'
            onClick={() => {
              prevStep();
            }}
            disabled={false}
          >
            Back
          </Button>

          <Button
            variant='secondary'
            onClick={() => {
              tryAddingSavedCard(tempSaleResponseInfo);
            }}
            disabled={addressLine1 === '' || addressZip === ''}
          >
            Next
          </Button>
        </Modal.Footer>
      )}

      {currentStep === FormSteps.PAYMENT_RESPONSE &&
        currentPaymentState === PaymentStates.Failed && (
          <Modal.Footer>
            <Button
              variant='light'
              onClick={() => {
                tryAgain();
              }}
              disabled={formLoading}
            >
              Try again
            </Button>
          </Modal.Footer>
        )}

      {currentStep === FormSteps.PAYMENT_RESPONSE && !showReceiptOptions && (
        <Modal.Footer>
          <Button
            variant='secondary'
            onClick={setShowModal}
            disabled={formLoading}
          >
            Finished
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
}

function validateStep(rules, payWithSavedCardEnabled) {
  const validationResult = rules.reduce((errors, vf) => {
    const error = !validateReducer(vf.field, vf.value);
    if (error) {
      errors[vf.field] = vf.validationMessage;
    }
    return errors;
  }, []);

  return validationResult;

  function validateReducer(field, value) {
    switch (field) {
      case 'totalAmount':
        return value > 0;
      case 'staffMember':
        return value?.length === 36;
      case 'petOwnerName':
        return value?.length > 0 && isFullName(value);
      case 'description':
        return value?.length > 0;
      case 'phoneNumber':
        if (!payWithSavedCardEnabled) return true;
        return value?.length > 0 && isPossiblePhoneNumber(value);
      default:
        return null;
    }
  }
}
